import {
	Box,
	Button,
	Flex,
	HStack,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	SimpleGrid,
	Text,
	useBreakpointValue,
} from "@chakra-ui/react";

import React, { useEffect } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "src/common/constants/constantEnums";

import type { FormErrors, MatrimonyFormData } from "src/types/matrimonyForm.types";
import { validateStep } from "./matrimonyForm.validation";

// NEW: step components
import Step1ProfileDetails from "./steps/Step1ProfileDetails";
import Step2CareerEducation from "./steps/Step2CareerEducation";
import Step3FamilyBackground from "./steps/Step3FamilyBackground";
import Step4PersonalLifestyle from "./steps/Step4PersonalLifestyle";
import Step5HoroscopeDetails from "./steps/Step5HoroscopeDetails";
import { MarriageProfileService } from "src/rest/marriageProfileService";
import { UseAppToast } from "src/common/useAppToast";

type MatrimonyFormProps = {
	isOpen: boolean;
	onClose: () => void;
	initialData?: Partial<MatrimonyFormData>;
	initialStep?: number;
	mode?: "create" | "edit";
};

const TOTAL_STEPS = 5;

const stepTitles: string[] = [
	"Profile Details",
	"Career & Education",
	"Family Background",
	"Personal Lifestyle",
	"Horoscope Details",
];

const MatrimonyForm: React.FC<MatrimonyFormProps> = ({
	isOpen,
	onClose,
	initialData,
	initialStep,
	mode = "create",
}) => {
	const navigate = useNavigate();
	const { showSuccessToast, showErrorToast, showInfoToast } = UseAppToast();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [currentStep, setCurrentStep] = useState<number>(1);
	const [gender, setGender] = useState<string>("Male");
	const [familyType, setFamilyType] = useState<string>("Nuclear");
	const [smoking, setSmoking] = useState<string>("No");
	const [drinking, setDrinking] = useState<string>("No");
	const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;

	const emptyFormData: MatrimonyFormData = {
		photoFile: null,
		additionalPhotos: [],
		fullName: "",
		dob: "",
		emailId: "",
		mobileNumber: "",
		maritalStatus: "",
		casteDetails: "",

		// NEW
		timeOfBirth: null,
		religion: "HINDU",
		about: "",
		socialMediaLinksList: [],
		smokingHabit: "",
		drinkingHabit: "",
		ownsHouse: "",
		totalFamilyMembers: "",

		highestEducation: "",
		collegeOrUniversity: "",
		employmentSector: "",
		designation: "",
		employerName: "",
		annualIncome: "",
		fatherName: "",
		motherName: "",
		fatherOccupation: "",
		motherOccupation: "",
		familyStatus: "",
		nativePlace: "",
		heightCm: "",
		bodyType: "",
		skinTone: "",
		dietaryHabits: "",
		hobbies: "",
		horoUnknown: "false",
		nakshatra: "",
		rasi: "",
		gothram: "",
		dosham: "",
	};

	const [formData, setFormData] = useState<MatrimonyFormData>(emptyFormData);
	const [errors, setErrors] = useState<FormErrors>({});

	useEffect(() => {
		if (!isOpen) return;

		setErrors({});
		setCurrentStep(Math.min(Math.max(initialStep ?? 1, 1), TOTAL_STEPS));

		// NEW: allow verified prefill from draft (created in marriage registration drawer)
		const rawDraft = window.localStorage.getItem("matrimonyProfileDraft");
		let verifiedPrefill: Partial<MatrimonyFormData> = {};
		try {
			const draft = rawDraft ? JSON.parse(rawDraft) : null;
			const v = draft?.verified;
			if (v) {
				verifiedPrefill = {
					fullName: String(v.candidateName || v.fullName || ""),
					dob: String(v.dateOfBirth || ""),
					// Align with verification drawer storage + backend contract
					emailId: String(v.email || v.emailId || ""),
					mobileNumber: String(v.mobileNo || v.mobileNumber || ""),
					fatherName: String(v.fatherName || ""),
					motherName: String(v.motherName || ""),
				};
			}
		} catch {
			verifiedPrefill = {};
		}

		setFormData({ ...emptyFormData, ...verifiedPrefill, ...(initialData || {}) });
	}, [isOpen, initialData, initialStep]);

	const updateField = (field: keyof MatrimonyFormData, value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		setErrors((prev) => {
			if (!prev[field]) return prev;
			const next = { ...prev };
			delete next[field];
			return next;
		});
	};

	const validateCurrentStep = (): boolean => {
		const nextErrors = validateStep(currentStep, formData);
		setErrors(nextErrors);
		return Object.keys(nextErrors).length === 0;
	};

	// When this is rendered as a standalone route (AppRouter uses isOpen={true} and onClose is a noop),
	// we need to navigate away on close/cancel.
	const closeOrNavigateAway = () => {
		// Always attempt to close via callback first.
		onClose();
		// If this form is being used as a page, we'd still be "open" forever, so navigate away.
		if (isOpen) {
			navigate(AppRoutes.PROFILE);
		}
	};

	const handleCancel = () => {
		setCurrentStep(1);
		setErrors({});
		closeOrNavigateAway();
	};

	// Close should behave like a close (no forced reset side-effects that can fight with modal state)
	const handleClose = () => {
		closeOrNavigateAway();
	};

	const handleRequestClose = () => {
		// Overlay click / Esc should just close the modal.
		// If you want to reset on dismiss, call handleCancel explicitly.
		handleClose();
	};

	const toBool = (value: string) => String(value || "").toLowerCase() === "yes";

	const toNumberOrNull = (value: string) => {
		const trimmed = (value || "").trim();
		if (!trimmed) return null;
		const n = Number(trimmed);
		return Number.isFinite(n) ? n : null;
	};

	const buildCreatePayload = () => {
		const payload: any = {
			fullName: formData.fullName,
			gender,
			dateOfBirth: formData.dob,
			email: formData.emailId,
			mobileNo: formData.mobileNumber,
			maritalStatus: formData.maritalStatus,
			casteDetails: formData.casteDetails,

			// NEW fields
			timeOfBirth: (formData.timeOfBirth || "").trim() ? formData.timeOfBirth : null,
			religion: formData.religion || "HINDU",
			about: (formData.about || "").trim() || null,
			socialMediaLinksList:
				Array.isArray(formData.socialMediaLinksList) && formData.socialMediaLinksList.length > 0
					? formData.socialMediaLinksList
						.map((x) => ({
							platform: String((x as any)?.platform || "").trim(),
							url: String((x as any)?.url || "").trim(),
						}))
						.filter((x) => x.platform && x.url)
					: null,
			// keep legacy booleans too (unchanged)

			highestEducation: formData.highestEducation,
			collegeOrUniversity: formData.collegeOrUniversity,
			employmentSector: formData.employmentSector,
			designation: formData.designation,
			employerName: formData.employerName,
			annualIncome: toNumberOrNull(formData.annualIncome),

			fatherName: formData.fatherName,
			motherName: formData.motherName,
			fatherOccupation: formData.fatherOccupation,
			motherOccupation: formData.motherOccupation,
			familyType,
			familyStatus: formData.familyStatus,
			nativePlace: formData.nativePlace,
			// NEW
			totalFamilyMembers: toNumberOrNull(formData.totalFamilyMembers || ""),

			heightInCm: toNumberOrNull(formData.heightCm),
			bodyType: formData.bodyType,
			skinTone: formData.skinTone,
			smoking: toBool(smoking),
			drinking: toBool(drinking),
			// NEW enums
			smokingHabit: (formData.smokingHabit || "").trim() || null,
			drinkingHabit: (formData.drinkingHabit || "").trim() || null,
			ownsHouse: formData.ownsHouse === "true" ? true : formData.ownsHouse === "false" ? false : null,
			dietaryHabits: formData.dietaryHabits,
			hobbiesInterests: formData.hobbies,

			nakshatra: formData.horoUnknown === "true" ? null : formData.nakshatra,
			rasi: formData.horoUnknown === "true" ? null : formData.rasi,
			gothram: formData.horoUnknown === "true" ? null : formData.gothram,
			dosham: formData.horoUnknown === "true" ? null : formData.dosham,
		};

		if (payload.annualIncome == null) delete payload.annualIncome;
		if (payload.heightInCm == null) delete payload.heightInCm;
		if (payload.totalFamilyMembers == null) delete payload.totalFamilyMembers;

		// Optional string/null fields: allow null per requirement (do not delete)

		return payload;
	};

	const handleProceed = async () => {
		if (!validateCurrentStep()) {
			return;
		}

		if (currentStep < TOTAL_STEPS) {
			setCurrentStep((step) => step + 1);
			return;
		}

		// Final step submit
		if (mode === "edit") {
			onClose();
			setCurrentStep(1);
			setErrors({});
			return;
		}

		try {
			setIsSubmitting(true);

			const data = buildCreatePayload();

			// Step 1: Create the marriage profile (only main photo in multipart).
			const res = await MarriageProfileService.createForLoggedInUser({
				data,
				photo: formData.photoFile ?? null,
			});

			const status = (res as any)?.status;
			if (typeof status === "number" && (status < 200 || status >= 300)) {
				throw new Error(`Create failed (HTTP ${status})`);
			}

			// Step 2: Upload additional images via the dedicated images endpoint.
			const createdProfileId = (res as any)?.data?.id;
			const additionalFiles = (formData.additionalPhotos ?? []).filter(Boolean) as File[];

			if (createdProfileId && additionalFiles.length > 0) {
				try {
					await MarriageProfileService.uploadImages(createdProfileId, additionalFiles, "newly created");
				} catch {
					// Profile was created successfully; warn about images but don't block.
					showErrorToast("Profile created but some additional photos failed to upload.");
				}
			}

			showSuccessToast("Marriage profile created");

			// Clear draft after successful creation.
			window.localStorage.removeItem("matrimonyProfileDraft");

			closeOrNavigateAway();
		} catch (err: any) {
			const status = err?.response?.status;
			const msg = String(err?.response?.data?.message || err?.message || "");
			const normalized = msg.toLowerCase();

			// Enforce single-profile constraint even if user bypassed verify step.
			// Backend typically returns 409 Conflict or a message indicating duplicate.
			if (
				status === 409 ||
				normalized.includes("already exists") ||
				normalized.includes("duplicate") ||
				normalized.includes("only one") ||
				normalized.includes("single")
			) {
				showInfoToast(
					msg ||
					"You can create only one marriage profile. Please manage the existing profile."
				);
				closeOrNavigateAway();
				return;
			}

			showErrorToast(msg || "Something went wrong.");
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleBack = () => {
		if (currentStep > 1) {
			setCurrentStep((step) => step - 1);
			setErrors({});
		}
	};

	const renderStepIndicator = () => (
		<Box mb={4}>
			<Text fontSize="sm" color="#6C757D" mb={2}>
				Step {currentStep} out of {TOTAL_STEPS}
			</Text>
			<HStack spacing={2}>
				{Array.from({ length: TOTAL_STEPS }).map((_, index) => (
					<Box
						key={index}
						flex="1"
						height="4px"
						borderRadius="full"
						bg={index + 1 <= currentStep ? "#4A2C2A" : "#E5E7EB"}
					/>
				))}
			</HStack>
		</Box>
	);

	const renderSegmentedButton = (
		optionValue: string,
		selectedValue: string,
		setSelectedValue: (value: string) => void,
		label: string
	) => (
		<Button
			flex="1"
			variant="outline"
			borderRadius="12px"
			borderColor={
				selectedValue === optionValue
					? "#4A2C2A"
					: "rgba(108,117,125,0.3)"
			}
			bg={
				selectedValue === optionValue
					? "rgba(74,44,42,0.06)"
					: "#F8F9FA"
			}
			color={selectedValue === optionValue ? "#4A2C2A" : "#6C757D"}
			_hover={{ bg: "rgba(74,44,42,0.06)" }}
			onClick={() => setSelectedValue(optionValue)}
		>
			{label}
		</Button>
	);

	const renderBody = () => {
		if (currentStep === 1) {
			return (
				<Step1ProfileDetails
					formData={formData}
					errors={errors}
					gender={gender}
					setGender={setGender}
					updateField={updateField}
					renderSegmentedButton={renderSegmentedButton}
				/>
			);
		}
		if (currentStep === 2) {
			return (
				<Step2CareerEducation
					formData={formData}
					errors={errors}
					updateField={updateField}
				/>
			);
		}
		if (currentStep === 3) {
			return (
				<Step3FamilyBackground
					formData={formData}
					errors={errors}
					familyType={familyType}
					setFamilyType={setFamilyType}
					updateField={updateField}
					renderSegmentedButton={renderSegmentedButton}
				/>
			);
		}
		if (currentStep === 4) {
			return (
				<Step4PersonalLifestyle
					formData={formData}
					errors={errors}
					smoking={smoking}
					setSmoking={setSmoking}
					drinking={drinking}
					setDrinking={setDrinking}
					updateField={updateField}
					renderSegmentedButton={renderSegmentedButton}
				/>
			);
		}
		return (
			<Step5HoroscopeDetails
				formData={formData}
				errors={errors}
				updateField={updateField}
			/>
		);
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleRequestClose}
			size={{ base: "full", md: "4xl" }}
			scrollBehavior="inside"
		>
			<ModalOverlay />
			<ModalContent
				borderRadius={{ base: 0, md: "24px" }}
				bg={{ base: "#F8F9FA", md: "white" }}
			>
				<ModalHeader
					fontSize={{ base: "lg", md: "xl" }}
					fontWeight="700"
					color="#4A2C2A"
					bg={{ base: "white", md: "transparent" }}
					borderBottom={{
						base: "1px solid rgba(108,117,125,0.18)",
						md: "none",
					}}
				>
					{stepTitles[currentStep - 1]}
				</ModalHeader>
				<ModalCloseButton onClick={handleClose} />
				<ModalBody pb={isMobile ? 28 : 6} px={{ base: 4, md: 6 }}>
					{renderStepIndicator()}
					{renderBody()}

					{/* Desktop buttons (existing layout) */}
					{!isMobile ? (
						<Flex mt={8} justify="space-between" gap={3}>
							<Button
								variant="outline"
								borderRadius="12px"
								borderColor="#4A2C2A"
								color="#4A2C2A"
								_hover={{ bg: "rgba(74,44,42,0.06)" }}
								onClick={handleCancel}
							>
								Cancel
							</Button>

							<HStack spacing={3}>
								{currentStep > 1 && (
									<Button
										variant="outline"
										borderRadius="12px"
										borderColor="#4A2C2A"
										color="#4A2C2A"
										_hover={{ bg: "rgba(74,44,42,0.06)" }}
										onClick={handleBack}
									>
										Back
									</Button>
								)}
								<Button
									borderRadius="12px"
									bg="#4A2C2A"
									color="white"
									_hover={{ bg: "#4A2C2A" }}
									onClick={handleProceed}
									isLoading={isSubmitting}
								>
									{currentStep === TOTAL_STEPS ? "Finish" : "Proceed"}
								</Button>
							</HStack>
						</Flex>
					) : null}
				</ModalBody>

				{/* Mobile bottom action bar */}
				{isMobile ? (
					<Box
						position="sticky"
						bottom="0"
						bg="white"
						borderTop="1px solid rgba(108,117,125,0.18)"
						px={4}
						py={3}
					>
						<SimpleGrid columns={2} spacing={3}>
							<Button
								variant="outline"
								borderRadius="12px"
								borderColor="#4A2C2A"
								color="#4A2C2A"
								_hover={{ bg: "rgba(74,44,42,0.06)" }}
								onClick={currentStep > 1 ? handleBack : handleCancel}
								height="48px"
							>
								{currentStep > 1 ? "Back" : "Cancel"}
							</Button>
							<Button
								borderRadius="12px"
								bg="#4A2C2A"
								color="white"
								_hover={{ bg: "#4A2C2A" }}
								onClick={handleProceed}
								isLoading={isSubmitting}
								height="48px"
							>
								{currentStep === TOTAL_STEPS ? "Finish" : "Next"}
							</Button>
						</SimpleGrid>
					</Box>
				) : null}
			</ModalContent>
		</Modal>
	);
};

export { MatrimonyForm };

export default MatrimonyForm;

